import {
	Copy,
	Group,
	ListChevronsDownUp,
	Plus,
	Send,
	SquaresUnite,
	Trash2,
} from "lucide-react";
import type { ReactNode } from "react";

import { Fab } from "@/v2/components/ui/fab";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { cn } from "@/v2/lib/utils";

interface SelectionActionButtonProps {
	label: string;
	disabled: boolean;
	onClick: () => void;
	withTopBorder?: boolean;
	children: ReactNode;
}

export interface EntriesActionDockProps {
	selectionMode: boolean;
	selectedCount: number;
	bulkDisabled?: boolean;
	onMerge: () => void;
	onCollapse: () => void;
	onGroup: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
	onExport: () => void;
	onAdd: () => void;
}

export function EntriesActionDock({
	selectionMode,
	selectedCount,
	bulkDisabled = false,
	onMerge,
	onCollapse,
	onGroup,
	onDuplicate,
	onDelete,
	onExport,
	onAdd,
}: EntriesActionDockProps) {
	const hasSelection = selectedCount > 0 && !bulkDisabled;
	const hasBulkSelection = selectedCount >= 2 && !bulkDisabled;

	return (
		<div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+12px)] right-4 z-30 flex flex-col items-end gap-2">
			<div
				className={cn(
					"flex origin-bottom-right flex-col gap-2 transition-all duration-200 ease-out",
					selectionMode
						? "scale-100 opacity-100"
						: "pointer-events-none scale-0 opacity-0",
				)}
			>
				<div className="flex flex-col overflow-hidden rounded-2xl border border-border shadow-lg">
					<SelectionActionButton
						label="Merge"
						onClick={onMerge}
						disabled={!hasBulkSelection}
					>
						<SquaresUnite />
					</SelectionActionButton>
					<SelectionActionButton
						label="Collapse"
						onClick={onCollapse}
						disabled={!hasBulkSelection}
						withTopBorder
					>
						<ListChevronsDownUp />
					</SelectionActionButton>
					<SelectionActionButton
						label="Group"
						onClick={onGroup}
						disabled={!hasBulkSelection}
						withTopBorder
					>
						<Group />
					</SelectionActionButton>
				</div>

				<div className="flex flex-col overflow-hidden rounded-2xl border border-border shadow-lg">
					<SelectionActionButton
						label="Duplicate"
						onClick={onDuplicate}
						disabled={!hasSelection}
					>
						<Copy />
					</SelectionActionButton>
					<SelectionActionButton
						label="Delete"
						onClick={onDelete}
						disabled={!hasSelection}
						withTopBorder
					>
						<Trash2 />
					</SelectionActionButton>
				</div>
			</div>

			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="inline-flex">
							<Fab
								className="bg-primary transition-all duration-200 disabled:opacity-50 disabled:active:scale-100"
								onClick={onExport}
								disabled={!hasSelection}
								aria-label="Export selected entries"
							>
								<Send className="size-6" />
							</Fab>
						</span>
					</TooltipTrigger>
					<TooltipContent side="left">Export selected entries</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="inline-flex">
							<Fab
								variant="secondary"
								className="border border-input bg-background text-foreground shadow-lg transition-all duration-200 hover:bg-muted"
								onClick={onAdd}
								aria-label="Add entry"
							>
								<Plus className="size-6" />
							</Fab>
						</span>
					</TooltipTrigger>
					<TooltipContent side="left">Add entry</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
}

function SelectionActionButton({
	label,
	disabled,
	onClick,
	withTopBorder,
	children,
}: SelectionActionButtonProps) {
	return (
		<TooltipButton
			type="button"
			variant="secondary"
			size="icon"
			onClick={onClick}
			disabled={disabled}
			tooltip={label}
			tooltipSide="left"
			aria-label={label}
			className={cn(
				"size-12 rounded-none shadow-none active:bg-secondary/80 [&_svg]:size-5",
				"disabled:active:bg-secondary",
				withTopBorder && "border-t border-border",
			)}
		>
			{children}
		</TooltipButton>
	);
}
